import type {
  ApiError,
  CandidateStatusView,
  ChangePasswordInput,
  CourseBlockDetail,
  CourseOverview,
  CreateInviteInput,
  CurrentUser,
  ErrorCode,
  InviteCheckResult,
  InternshipInput,
  LoginInput,
  RegisterInput,
  SubmitTestInput,
  TestResult,
  UpdateProfileInput,
  SlotView,
  CreateSlotInput,
  MeetingView,
  CreateMeetingInput,
  MeetingOutcomeInput,
  MeetingsSummary,
  RecruiterAnalytics,
  MessageTemplateInput,
  MessageTemplateView,
  ScriptInput,
  ScriptView,
  CandidateRow,
  CandidateHistoryItem,
  CreateCandidateInput,
  UpdateCandidateInput,
  ChangeStageInput,
  TrainingAnalytics,
  LeaveReasonView,
  NotificationView,
  DurationsReport,
  FunnelReport,
  InternshipDay,
  EmployeeRow,
  HeadStats,
  SetPlanInput,
  StatusChangeLogItem,
  AdminUserRow,
  CreateUserInput,
  UpdateUserInput,
  UserChangeView,
  UserCourseProgress,
  RoleView,
  CreateRoleInput,
  UpdateRoleInput,
  Permission,
  AdminBlockView,
  MediaUsage,
  AdminQuestionView,
  UpsertBlockInput,
  UpsertQuestionInput,
  ReorderBlocksInput,
  SettingView,
  UpdateSettingsInput,
} from '@aurea/contracts';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/v1';

/** Тот же адрес наружу: загрузка файлов идёт мимо apiFetch, ей нужен путь. */
export const API_BASE = BASE_URL;

/** Ошибка API с машиночитаемым кодом — интерфейс реагирует на код, не на текст. */
export class ApiRequestError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Значение csrf-куки. Она намеренно читаемая — это double submit cookie. */
export function csrfToken(): string | null {
  return readCsrfToken();
}

function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)aurea_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, method = 'GET', ...rest } = options;

  const requestHeaders = new Headers(headers);
  if (body !== undefined) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  // Мутации подтверждают происхождение заголовком, см. docs/05-auth-rbac.md.
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readCsrfToken();
    if (csrf) requestHeaders.set('X-CSRF-Token', csrf);
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    method,
    headers: requestHeaders,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as ApiError | null)?.error;
    throw new ApiRequestError(
      error?.code ?? 'INTERNAL',
      error?.message ?? 'Что-то пошло не так. Попробуйте ещё раз.',
      response.status,
      error?.details,
    );
  }

  return payload as T;
}

export interface HealthResponse {
  status: 'ok' | 'degraded';
  db: boolean;
  uptimeSeconds: number;
}

export interface InviteListItem {
  id: string;
  number: number;
  token: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  usedCount: number;
  maxUses: number | null;
  note: string | null;
  createdBy: { id: string; firstName: string; lastName: string };
  meeting: { id: string; date: string; time: string } | null;
  isActive: boolean;
}

export const api = {
  health: () => apiFetch<HealthResponse>('/health'),
  version: () => apiFetch<{ name: string; version: string; commit: string }>('/version'),

  login: (body: LoginInput) => apiFetch<CurrentUser>('/auth/login', { method: 'POST', body }),
  logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
  me: () => apiFetch<CurrentUser>('/auth/me'),
  refresh: () => apiFetch<{ ok: true }>('/auth/refresh', { method: 'POST' }),
  changePassword: (body: ChangePasswordInput) =>
    apiFetch<void>('/auth/password', { method: 'PATCH', body }),

  checkInvite: (token: string) =>
    apiFetch<InviteCheckResult>(`/public/invites/${encodeURIComponent(token)}`),
  register: (body: RegisterInput) =>
    apiFetch<CurrentUser>('/public/register', { method: 'POST', body }),

  listInvites: () => apiFetch<InviteListItem[]>('/invites'),
  createInvite: (body: CreateInviteInput) =>
    apiFetch<{ id: string; number: number; token: string; expiresAt: string }>('/invites', {
      method: 'POST',
      body,
    }),
  linkTelegram: () =>
    apiFetch<{ url: string; expiresAt: string }>('/telegram/link', { method: 'POST' }),
  unlinkTelegram: () => apiFetch<void>('/telegram/link', { method: 'DELETE' }),
  recoverPassword: (email: string) =>
    apiFetch<void>('/public/password/recover', { method: 'POST', body: { email } }),

  revokeInvite: (id: string) => apiFetch<void>(`/invites/${id}/revoke`, { method: 'POST' }),

  // Кабинет соискателя
  course: () => apiFetch<CourseOverview>('/learn/course'),
  block: (id: string) => apiFetch<CourseBlockDetail>(`/learn/blocks/${id}`),
  submitTest: (id: string, body: SubmitTestInput) =>
    apiFetch<TestResult>(`/learn/blocks/${id}/submit`, { method: 'POST', body }),
  companyPage: () => apiFetch<{ html: string }>('/learn/company'),
  candidateStatus: () => apiFetch<CandidateStatusView>('/learn/status'),
  createInternship: (body: InternshipInput) =>
    apiFetch<{ ok: true }>('/learn/internship', { method: 'POST', body }),
  updateInternship: (body: InternshipInput) =>
    apiFetch<{ ok: true }>('/learn/internship', { method: 'PATCH', body }),

  // Профиль
  updateProfile: (body: UpdateProfileInput) =>
    apiFetch<void>('/profile', { method: 'PATCH', body }),
  profileChanges: () =>
    apiFetch<{ date: string; field: string; oldValue: string; newValue: string }[]>(
      '/profile/changes',
    ),
  preferences: () =>
    apiFetch<{ notifyEmail: boolean; notifyMessenger: boolean }>('/profile/preferences'),

  // Встречи
  slots: () => apiFetch<SlotView[]>('/slots'),
  createSlot: (body: CreateSlotInput) => apiFetch<SlotView>('/slots', { method: 'POST', body }),
  deleteSlot: (id: string) => apiFetch<void>(`/slots/${id}`, { method: 'DELETE' }),
  meetings: (date: string) => apiFetch<MeetingView[]>(`/meetings?date=${date}`),
  createMeeting: (body: CreateMeetingInput) =>
    apiFetch<MeetingView>('/meetings', { method: 'POST', body }),
  deleteMeeting: (id: string) => apiFetch<void>(`/meetings/${id}`, { method: 'DELETE' }),
  setOutcome: (id: string, body: MeetingOutcomeInput) =>
    apiFetch<MeetingView>(`/meetings/${id}/outcome`, { method: 'PUT', body }),
  meetingsSummary: (params: { from?: string; to?: string } = {}) =>
    apiFetch<MeetingsSummary>(`/meetings/summary${query(params)}`),
  meetingsAnalytics: () => apiFetch<RecruiterAnalytics>('/meetings/analytics'),
  messageTemplates: () => apiFetch<MessageTemplateView[]>('/message-templates'),
  scripts: () => apiFetch<ScriptView[]>('/scripts'),

  // Кандидаты
  candidates: (params: { status?: string; q?: string; from?: string; to?: string } = {}) =>
    apiFetch<CandidateRow[]>(`/candidates${query(params)}`),
  candidateHistory: (id: string) => apiFetch<CandidateHistoryItem[]>(`/candidates/${id}/history`),
  createCandidate: (body: CreateCandidateInput) =>
    apiFetch<{ id: string; password: string }>('/candidates', { method: 'POST', body }),
  updateCandidate: (id: string, body: UpdateCandidateInput) =>
    apiFetch<void>(`/candidates/${id}`, { method: 'PATCH', body }),
  changeStage: (id: string, body: ChangeStageInput) =>
    apiFetch<void>(`/candidates/${id}/stage`, { method: 'POST', body }),
  deleteCandidate: (id: string) => apiFetch<void>(`/candidates/${id}`, { method: 'DELETE' }),
  trainingAnalytics: () => apiFetch<TrainingAnalytics>('/candidates/analytics'),
  leaveReasons: () => apiFetch<LeaveReasonView[]>('/leave-reasons'),

  // Аналитика
  funnel: (params: { from?: string; to?: string } = {}) =>
    apiFetch<FunnelReport>(`/analytics/funnel${query(params)}`),
  durations: () => apiFetch<DurationsReport>('/analytics/durations'),
  internshipDays: (params: { from?: string; to?: string } = {}) =>
    apiFetch<InternshipDay[]>(`/analytics/internships${query(params)}`),

  employees: (
    params: { status?: string; q?: string; from?: string; to?: string; onlyLeft?: boolean } = {},
  ) =>
    apiFetch<EmployeeRow[]>(
      `/analytics/employees${query({ ...params, onlyLeft: params.onlyLeft ? 'true' : undefined })}`,
    ),
  headStats: (params: { from?: string; to?: string } = {}) =>
    apiFetch<HeadStats>(`/analytics/head${query(params)}`),
  setPlan: (body: SetPlanInput) => apiFetch<void>('/analytics/plan', { method: 'POST', body }),
  statusLog: (q?: string) =>
    apiFetch<StatusChangeLogItem[]>(`/analytics/status-log${query({ q })}`),

  // Администрирование
  adminUsers: (params: { q?: string; roleId?: string } = {}) =>
    apiFetch<AdminUserRow[]>(`/users${query(params)}`),
  createUser: (body: CreateUserInput) =>
    apiFetch<{ id: string; password: string }>('/users', { method: 'POST', body }),
  updateUser: (id: string, body: UpdateUserInput) =>
    apiFetch<void>(`/users/${id}`, { method: 'PATCH', body }),
  userCourse: (id: string) => apiFetch<UserCourseProgress>(`/users/${id}/course`),
  markUserBlock: (id: string, blockId: string, completed: boolean) =>
    apiFetch<void>(`/users/${id}/course/${blockId}`, { method: 'PUT', body: { completed } }),

  blockUser: (id: string) => apiFetch<void>(`/users/${id}/block`, { method: 'POST' }),
  unblockUser: (id: string) => apiFetch<void>(`/users/${id}/unblock`, { method: 'POST' }),
  resetUserPassword: (id: string) =>
    apiFetch<{ password: string }>(`/users/${id}/reset-password`, { method: 'POST' }),
  deleteUser: (id: string) => apiFetch<void>(`/users/${id}`, { method: 'DELETE' }),
  userChanges: (q?: string) => apiFetch<UserChangeView[]>(`/users/changes${query({ q })}`),

  roles: () => apiFetch<RoleView[]>('/roles'),
  createRole: (body: CreateRoleInput) =>
    apiFetch<{ id: string }>('/roles', { method: 'POST', body }),
  updateRole: (id: string, body: UpdateRoleInput) =>
    apiFetch<void>(`/roles/${id}`, { method: 'PATCH', body }),
  deleteRole: (id: string) => apiFetch<void>(`/roles/${id}`, { method: 'DELETE' }),
  permissionGroups: () => apiFetch<{ group: string; permissions: Permission[] }[]>('/permissions'),

  createTemplate: (body: MessageTemplateInput) =>
    apiFetch<{ id: string }>('/message-templates', { method: 'POST', body }),
  updateTemplate: (id: string, body: MessageTemplateInput) =>
    apiFetch<void>(`/message-templates/${id}`, { method: 'PATCH', body }),
  deleteTemplate: (id: string) => apiFetch<void>(`/message-templates/${id}`, { method: 'DELETE' }),

  createScript: (body: ScriptInput) =>
    apiFetch<{ id: string }>('/scripts', { method: 'POST', body }),
  updateScript: (id: string, body: ScriptInput) =>
    apiFetch<void>(`/scripts/${id}`, { method: 'PATCH', body }),
  deleteScript: (id: string) => apiFetch<void>(`/scripts/${id}`, { method: 'DELETE' }),

  mediaUsage: () => apiFetch<MediaUsage>('/admin/course/media-usage'),
  deleteBlockVideo: (blockId: string) =>
    apiFetch<void>(`/admin/course/blocks/${blockId}/video`, { method: 'DELETE' }),

  adminBlocks: () => apiFetch<AdminBlockView[]>('/admin/course/blocks'),
  createBlock: (body: UpsertBlockInput) =>
    apiFetch<{ id: string }>('/admin/course/blocks', { method: 'POST', body }),
  updateBlock: (id: string, body: UpsertBlockInput) =>
    apiFetch<void>(`/admin/course/blocks/${id}`, { method: 'PATCH', body }),
  deleteBlock: (id: string) => apiFetch<void>(`/admin/course/blocks/${id}`, { method: 'DELETE' }),
  reorderBlocks: (body: ReorderBlocksInput) =>
    apiFetch<void>('/admin/course/blocks/reorder', { method: 'POST', body }),
  adminQuestions: (blockId: string) =>
    apiFetch<AdminQuestionView[]>(`/admin/course/blocks/${blockId}/questions`),
  createQuestion: (blockId: string, body: UpsertQuestionInput) =>
    apiFetch<{ id: string }>(`/admin/course/blocks/${blockId}/questions`, {
      method: 'POST',
      body,
    }),
  updateQuestion: (id: string, body: UpsertQuestionInput) =>
    apiFetch<void>(`/admin/course/questions/${id}`, { method: 'PATCH', body }),
  deleteQuestion: (id: string) =>
    apiFetch<void>(`/admin/course/questions/${id}`, { method: 'DELETE' }),

  settings: () => apiFetch<SettingView[]>('/settings'),
  updateSettings: (body: UpdateSettingsInput) =>
    apiFetch<void>('/settings', { method: 'PATCH', body }),

  // Уведомления
  notifications: () => apiFetch<NotificationView[]>('/notifications'),
  markNotificationsRead: () => apiFetch<void>('/notifications/read', { method: 'POST' }),
};

/** Собирает строку запроса, пропуская пустые значения. */
function query(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, value]) => value);
  if (entries.length === 0) return '';
  return `?${new URLSearchParams(entries as [string, string][]).toString()}`;
}
